import type { UnifiedChatMessage } from "./types";
import type { AnalysisChatEvent } from "@/backend/analysis-chat-v2/types";
import type {
  AssistantFrontendChatMessage,
  AssistantIteration,
} from "@/components/analysis/chat/v2/messages/types";
import { aggregateToolProgressTimelineItems } from "@/components/analysis/chat/v2/utils/statusMessages";
import { assertNever } from "@/packages/ts-utils";
import type { AnalysisChatV2MessageId } from "@/utils/types";

type AssistantMessage = AssistantFrontendChatMessage<AnalysisChatV2MessageId>;
type UpdateIteration = (iteration: AssistantIteration) => AssistantIteration;
export type UpdateAssistantMessage = (
  message: AssistantMessage
) => AssistantMessage;

const createIteration = (): AssistantIteration => ({
  reportItems: [],
  statusMessages: [],
  timestamp: new Date(),
});

/** Updates the assistant message with this id, or appends it if `update` changes the empty placeholder. */
export const upsertAssistantMessage = ({
  messages,
  assistantMessageId,
  update,
}: {
  messages: UnifiedChatMessage[];
  assistantMessageId: AnalysisChatV2MessageId;
  update: UpdateAssistantMessage;
}): UnifiedChatMessage[] => {
  const existing = messages.find(
    (message): message is AssistantMessage =>
      message.role === "assistant" && message.id === assistantMessageId
  );
  if (existing) {
    return messages.map((message) =>
      message === existing ? update(existing) : message
    );
  }
  const placeholder: AssistantMessage = {
    id: assistantMessageId,
    role: "assistant",
    iterations: [],
    timestamp: new Date(),
  };
  const created = update(placeholder);
  return created === placeholder ? messages : [...messages, created];
};

// Defaults to the last iteration, creating one if the message has none yet.
const updateIteration = (
  message: AssistantMessage,
  update: UpdateIteration,
  index?: number
): AssistantMessage => {
  const iterations =
    message.iterations.length > 0 ? message.iterations : [createIteration()];
  const targetIndex = index ?? iterations.length - 1;
  return {
    ...message,
    iterations: iterations.map((iteration, i) =>
      i === targetIndex ? update(iteration) : iteration
    ),
  };
};

export const applyChatEvent = (
  message: AssistantMessage,
  event: AnalysisChatEvent
): AssistantMessage => {
  switch (event.type) {
    case "iteration-start":
      return {
        ...message,
        iterations: [...message.iterations, createIteration()],
      };
    case "tool-progress": {
      const { type: _type, ...status } = event;
      // A late update (e.g. a summarizer after the next iteration started) stays in the iteration that first showed it.
      const ownerIndex = message.iterations.findIndex((iteration) =>
        iteration.statusMessages.some((existing) => existing.id === status.id)
      );
      return updateIteration(
        message,
        (iteration) => ({
          ...iteration,
          statusMessages: aggregateToolProgressTimelineItems(
            iteration.statusMessages,
            status
          ),
        }),
        ownerIndex >= 0 ? ownerIndex : undefined
      );
    }
    case "report-item":
      return updateIteration(message, (iteration) => ({
        ...iteration,
        reportItems: [...iteration.reportItems, event.item],
      }));
    case "report-complete":
      return updateIteration(message, (iteration) => ({
        ...iteration,
        reportItems: event.items,
      }));
    case "error":
      return {
        ...updateIteration(message, (iteration) => ({
          ...iteration,
          error: event.error,
        })),
        agentQuestions: undefined,
      };
    case "agent-questions":
      return { ...message, agentQuestions: event.questions };
    case "init":
    case "debug":
    case "finish":
    case "invalidate-queries":
    case "invalidate-feature-table":
    case "navigate":
      return message;
    default:
      return assertNever(event);
  }
};

export const failAssistantMessage = (
  message: AssistantMessage,
  error: string
): AssistantMessage => ({
  ...updateIteration(message, (iteration) => ({
    ...iteration,
    error,
    statusMessages: [],
  })),
  agentQuestions: undefined,
});

export const resetAssistantMessage = (
  message: AssistantMessage
): AssistantMessage => ({
  ...message,
  timestamp: new Date(),
  iterations: [createIteration()],
  agentQuestions: undefined,
});
